// src/app/services/congress.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface Bill {
  billNumber:     string;
  billType:       string;
  congress:       number;
  title:          string;
  introducedDate: string;
  latestAction:   { actionDate: string; text: string };
  url:            string;
  policyArea?:    string;
  status:         string;
  statusColor:    string;
  lydiaPosition?: 'supports' | 'opposes' | 'watching' | null;
}

@Injectable({ providedIn: 'root' })
export class CongressService {

  private readonly proxyBase = '/congress-proxy.php';

  // Michigan delegation bioguide IDs for sponsored-by-Michigan filter
  private readonly michiganBioguideIds = [
    'P000595', 'S001208', // Senators
    'B001301', 'M001194', 'S001221', 'H001058', 'W000798',
    'D000624', 'B001321', 'M001237', 'M001136', 'J000307',
    'S001215', 'T000481', 'T000488'  // House
  ];

  // Lydia's stated positions on specific bills
  private readonly lydiaPositions: Record<string, 'supports' | 'opposes' | 'watching'> = {
    'HR-15':    'supports',  // Equality Act
    // SAVE Act lineage — all three versions opposed
    'S-1383':   'opposes',   // SAVE America Act (S. 1383) — ACTIVE, currently tracked
    'HR-22':    'opposes',   // SAVE Act (H.R. 22) — superseded, Senate stalled
    'HR-8281':  'opposes',   // SAVE Act (H.R. 8281) — original 118th Congress, dead
    'HR-7296':  'opposes',   // SAVE America Act (H.R. 7296) — superseded by S. 1383
    'HR-46':    'supports',  // Rural Broadband Window of Opportunity Act
    'HR-117':   'supports',  // Fourth Amendment Restoration Act
    'HR-148':   'supports',  // Keep Your Coins Act (crypto)
    'HR-155':   'supports',  // Let America Vote Act
  };

  private readonly michiganBills: {type: string, number: string}[] = [
    // Great Lakes & Environment
    { type: 'hr',  number: '187'  }, // MAPWaters Act — signed into law
    { type: 'hr',  number: '197'  }, // Lake Winnibigoshish Land Exchange
    { type: 'hr',  number: '167'  }, // Community Reclamation Partnerships Act
    { type: 'hr',  number: '178'  }, // Wildfire suppression
    { type: 'hr',  number: '179'  }, // Proven Forest Management Act
    // Labor & Economy
    { type: 'hr',  number: '85'   }, // Small Business Flexibility Act
    { type: 'hr',  number: '110'  }, // Small Business Prosperity Act
    { type: 'hr',  number: '138'  }, // Lowering Costs for Caregivers Act
    // Civil Rights & Voting
    { type: 'hr',  number: '15'   }, // Equality Act
    { type: 's',   number: '1383' }, // SAVE America Act — ACTIVE (replaces HR-22 & HR-7296)
    { type: 'hr',  number: '155'  }, // Let America Vote Act
    // Digital & Tech
    { type: 'hr',  number: '46'   }, // Rural Broadband Window of Opportunity Act
    { type: 'hr',  number: '117'  }, // Fourth Amendment Restoration Act
    { type: 'hr',  number: '148'  }, // Keep Your Coins Act (crypto)
    // Healthcare
    { type: 'hr',  number: '136'  }, // Veteran Overmedication Prevention Act
    { type: 'hr',  number: '165'  }, // Wounded Knee Massacre Memorial Act — signed into law
    // Budget & Social
    { type: 'hr',  number: '191'  }, // Inflation Reduction Act 2025
    { type: 'hr',  number: '40'   }, // Commission to Study Reparations
  ];

  private readonly topicKeywords: Record<string, string[]> = {
    voting:      ['voting', 'voter', 'election', 'registration', 'ballot', 'suffrage', 'citizenship', 'polling', 'electoral', 'disenfranchise', 'purge', 'ID', 'redistricting', 'gerrymandering', 'precinct', 'absentee', 'mail-in'],
    digital:     ['internet', 'digital', 'privacy', 'data', 'cyber', 'online', 'broadband', 'network', 'surveillance', 'tech', 'telecommunications', 'encryption', 'algorithm', 'platform', 'social media'],
    environment: ['environment', 'climate', 'water', 'lake', 'energy', 'clean', 'pollution', 'carbon', 'emission', 'wildlife', 'conservation', 'forest', 'renewable', 'solar', 'wind', 'endangered', 'EPA'],
    healthcare:  ['health', 'medicare', 'medicaid', 'hospital', 'drug', 'prescription', 'insurance', 'medical', 'care', 'patient', 'mental', 'opioid', 'pharmaceutical', 'nurse', 'doctor', 'clinic'],
    labor:       ['labor', 'wage', 'worker', 'union', 'employment', 'workforce', 'strike', 'job', 'workplace', 'employee', 'salary', 'pension', 'occupational', 'OSHA', 'collective', 'apprentice', 'overtime', 'hire', 'unemploy'],
    civil:       ['civil', 'rights', 'equality', 'discrimination', 'voting', 'lgbtq', 'liberty', 'freedom', 'justice', 'amendment', 'constitution', 'protection', 'minority', 'gender', 'race', 'religion', 'speech', 'assembly'],
    economy:     ['tax', 'economic', 'budget', 'finance', 'trade', 'tariff', 'business', 'inflation', 'deficit', 'debt', 'appropriation', 'spending', 'revenue', 'fiscal', 'manufacturing', 'export', 'import'],
    ai:          ['artificial intelligence', 'technology', 'innovation', 'algorithm', 'automation', 'robot', 'machine learning', 'semiconductor', 'computing', 'software', 'hardware', 'STEM', 'science', 'research'],
    social:      ['social security', 'retirement', 'pension', 'senior', 'medicare', 'disability', 'benefit', 'veteran', 'housing', 'poverty', 'food', 'nutrition', 'welfare', 'supplemental', 'SSI', 'SNAP'],
  };

  private readonly pinnedBills: Record<string, {type: string, number: string}[]> = {
    // Voting rights — S. 1383 pinned first as the active tracked bill
    voting:      [
      { type: 's',  number: '1383' }, // SAVE America Act — ACTIVE / currently tracked
      { type: 'hr', number: '155'  }, // Let America Vote Act
      { type: 'hr', number: '15'   }, // Equality Act (includes voting provisions)
    ],
    civil:       [
      { type: 's',  number: '1383' }, // SAVE America Act — ACTIVE (replaces HR-22 & HR-7296)
      { type: 'hr', number: '15'   }, // Equality Act
      { type: 'hr', number: '117'  }, // Fourth Amendment Restoration Act
    ],
    digital:     [
      { type: 'hr', number: '46'   }, // Rural Broadband
      { type: 'hr', number: '148'  }, // Keep Your Coins Act
      { type: 'hr', number: '117'  }, // Fourth Amendment Restoration Act
    ],
    labor:       [],
    environment: [],
    healthcare:  [],
    economy:     [],
    ai:          [],
    social:      [],
  };

  private readonly typeToSlug: Record<string, string> = {
    'hr':       'house-bill',
    's':        'senate-bill',
    'hres':     'house-resolution',
    'sres':     'senate-resolution',
    'hjres':    'house-joint-resolution',
    'sjres':    'senate-joint-resolution',
    'hconres':  'house-concurrent-resolution',
    'sconres':  'senate-concurrent-resolution',
  };

  constructor(private http: HttpClient) {}

  // ── STATUS PARSING ───────────────────────────────────────────────────────────
  parseStatus(actionText: string): { label: string; color: string } {
    const t = (actionText ?? '').toLowerCase();
    if (t.includes('became public law') || t.includes('signed by president'))
      return { label: 'Signed into Law',   color: 'status-law'       };
    if (t.includes('vetoed'))
      return { label: 'Vetoed',            color: 'status-vetoed'    };
    if (t.includes('cloture') && (t.includes('failed') || t.includes('not invoked')))
      return { label: 'Cloture Failed',    color: 'status-stalled'   };
    if (t.includes('passed senate') || (t.includes('read the second time') && t.includes('placed on')))
      return { label: 'Passed Senate',     color: 'status-passed'    };
    if (t.includes('passed house') || t.includes('received in the senate'))
      return { label: 'Passed House',      color: 'status-passed'    };
    if (t.includes('ordered to be reported') || t.includes('reported by'))
      return { label: 'Out of Committee',  color: 'status-committee' };
    if (t.includes('hearing') || t.includes('subcommittee'))
      return { label: 'In Committee',      color: 'status-committee' };
    if (t.includes('referred to'))
      return { label: 'Introduced',        color: 'status-introduced'};
    return   { label: 'Active',            color: 'status-active'    };
  }

  // ── BILL NUMBER DETECTION ────────────────────────────────────────────────────
  private parseBillNumber(query: string): { type: string; number: string } | null {
    const clean = query.trim().toUpperCase().replace(/[\s.\-]/g, '');
    const typeMap: Record<string, string> = {
      'HR': 'hr', 'HJRES': 'hjres', 'HRES': 'hres', 'HCONRES': 'hconres',
      'S': 's',   'SJRES': 'sjres', 'SRES': 'sres', 'SCONRES': 'sconres'
    };
    const types = Object.keys(typeMap).sort((a, b) => b.length - a.length);
    for (const t of types) {
      if (clean.startsWith(t)) {
        const num = clean.slice(t.length);
        if (/^\d+$/.test(num)) return { type: typeMap[t], number: num };
      }
    }
    return null;
  }

  // ── FETCH SINGLE BILL ────────────────────────────────────────────────────────
  getBillByNumber(type: string, number: string): Observable<Bill[]> {
    const params = new HttpParams()
      .set('path',   `/bill/119/${type}/${number}`)
      .set('format', 'json');
    return this.http.get<any>(this.proxyBase, { params }).pipe(
      map(res => res?.bill ? [this.mapBill(res.bill)] : []),
      catchError(() => of([]))
    );
  }

  // ── FETCH BILLS BY TOPIC ─────────────────────────────────────────────────────
  getBillsByTopic(topicKey: string, limit = 20): Observable<Bill[]> {
    // Michigan tab uses fully curated list — no keyword search
    if (topicKey === 'michigan') {
      const requests = this.michiganBills.map(p =>
        this.getBillByNumber(p.type, p.number)
      );
      return new Observable(observer => {
        forkJoin(requests).subscribe({
          next: (results) => {
            observer.next((results as Bill[][]).flat());
            observer.complete();
          },
          error: () => { observer.next([]); observer.complete(); }
        });
      });
    }

    // Voting tab — lead with pinned bills, then keyword search for additional results
    // S. 1383 is always first in pinnedBills['voting'] so it appears at top of results
    const keywords  = this.topicKeywords[topicKey] ?? this.topicKeywords['digital'];
    const pinned    = this.pinnedBills[topicKey]   ?? [];

    const keyword$ = this.http.get<any>(this.proxyBase, {
      params: new HttpParams()
        .set('path',         '/bill/119')
        .set('keywords',     keywords.join(','))
        .set('search_limit', limit.toString())
    }).pipe(
      map(res => (res?.bills ?? []).map((b: any) => this.mapBill(b))),
      catchError(() => of([]))
    );

    const pinnedRequests = pinned.map(p =>
      this.getBillByNumber(p.type, p.number)
    );

    if (pinnedRequests.length === 0) return keyword$;

    return new Observable(observer => {
      forkJoin([keyword$, ...pinnedRequests]).subscribe({
        next: (results) => {
          const [keywordBills, ...pinnedArrays] = results as Bill[][];
          const pinnedBills = pinnedArrays.flat();
          const seen = new Set<string>();
          const merged: Bill[] = [];
          for (const b of [...pinnedBills, ...keywordBills]) {
            const key = `${b.billType}-${b.billNumber}`;
            if (!seen.has(key)) { seen.add(key); merged.push(b); }
          }
          observer.next(merged.slice(0, limit + pinned.length));
          observer.complete();
        },
        error: () => { observer.next([]); observer.complete(); }
      });
    });
  }

  // ── SEARCH BILLS ─────────────────────────────────────────────────────────────
  searchBills(query: string, limit = 20): Observable<Bill[]> {
    const billNum = this.parseBillNumber(query);
    if (billNum) return this.getBillByNumber(billNum.type, billNum.number);

    const keywords = query.toLowerCase().split(' ').filter(w => w.length > 2);
    const params = new HttpParams()
      .set('path',         '/bill/119')
      .set('keywords',     keywords.join(','))
      .set('search_limit', limit.toString());

    return this.http.get<any>(this.proxyBase, { params }).pipe(
      map(res => (res?.bills ?? []).map((b: any) => this.mapBill(b))),
      catchError(() => of([]))
    );
  }

  // ── HELPERS ──────────────────────────────────────────────────────────────────
  private buildCongressUrl(b: any): string {
    const congress = b.congress ?? 119;
    const number   = b.number   ?? '';
    const type     = (b.type    ?? '').toLowerCase();
    const slug     = this.typeToSlug[type] ?? type;
    return `https://www.congress.gov/bill/${congress}th-congress/${slug}/${number}`;
  }

  private getLydiaPosition(b: any): 'supports' | 'opposes' | 'watching' | null {
    // Build key as TYPE-NUMBER e.g. "S-1383", "HR-22"
    const key = `${(b.type ?? '').toUpperCase()}-${b.number}`;
    return this.lydiaPositions[key] ?? null;
  }

  private mapBill(b: any): Bill {
    const status = this.parseStatus(b.latestAction?.text ?? '');
    return {
      billNumber:     b.number          ?? '',
      billType:       b.type            ?? '',
      congress:       b.congress        ?? 0,
      title:          b.title           ?? 'Untitled',
      introducedDate: b.introducedDate  ?? b.updateDate ?? '',
      latestAction:   b.latestAction    ?? { actionDate: '', text: '' },
      url:            this.buildCongressUrl(b),
      policyArea:     b.policyArea?.name ?? '',
      status:         status.label,
      statusColor:    status.color,
      lydiaPosition:  this.getLydiaPosition(b),
    };
  }
}
